/**
 * ImageGalleryThumbnails Component
 *
 * Horizontal scrollable thumbnail strip for the ImageGallery.
 * Active thumbnail has full opacity and an orange border;
 * inactive thumbnails are dimmed.
 */

import React, { useCallback, useRef } from 'react';
import { FlatList, Image, Pressable, StyleSheet, View } from 'react-native';
import { borderRadius, colors, spacing } from '../../tokens';

const THUMBNAIL_SIZE = 56;
const THUMBNAIL_GAP = spacing.sm;

export interface ImageGalleryThumbnailsProps {
  images: { uri: string; key: string }[];
  activeIndex: number;
  onThumbnailPress: (index: number) => void;
  testID?: string;
}

export const ImageGalleryThumbnails: React.FC<ImageGalleryThumbnailsProps> = ({
  images,
  activeIndex,
  onThumbnailPress,
  testID,
}) => {
  const flatListRef = useRef<FlatList>(null);

  const scrollToIndex = useCallback(
    (index: number) => {
      if (flatListRef.current && images.length > 0) {
        flatListRef.current.scrollToIndex({
          index,
          animated: true,
          viewPosition: 0.5,
        });
      }
    },
    [images.length]
  );

  React.useEffect(() => {
    scrollToIndex(activeIndex);
  }, [activeIndex, scrollToIndex]);

  const getItemLayout = useCallback(
    (_: unknown, index: number) => ({
      length: THUMBNAIL_SIZE + THUMBNAIL_GAP,
      offset: (THUMBNAIL_SIZE + THUMBNAIL_GAP) * index,
      index,
    }),
    []
  );

  const renderThumbnail = useCallback(
    ({
      item,
      index,
    }: {
      item: { uri: string; key: string };
      index: number;
    }) => {
      const isActive = index === activeIndex;

      return (
        <Pressable
          onPress={() => onThumbnailPress(index)}
          accessibilityRole="button"
          accessibilityLabel={`View image ${index + 1}`}
        >
          <View
            style={[
              styles.thumbnailContainer,
              isActive && styles.thumbnailActive,
            ]}
          >
            <Image
              source={{ uri: item.uri }}
              style={[
                styles.thumbnailImage,
                !isActive && styles.thumbnailInactive,
              ]}
              resizeMode="cover"
            />
          </View>
        </Pressable>
      );
    },
    [activeIndex, onThumbnailPress]
  );

  const keyExtractor = useCallback(
    (item: { uri: string; key: string }) => item.key,
    []
  );

  return (
    <View style={styles.container} testID={testID}>
      <FlatList
        ref={flatListRef}
        data={images}
        renderItem={renderThumbnail}
        keyExtractor={keyExtractor}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.listContent}
        getItemLayout={getItemLayout}
        ItemSeparatorComponent={Separator}
      />
    </View>
  );
};

const Separator = () => <View style={{ width: THUMBNAIL_GAP }} />;

ImageGalleryThumbnails.displayName = 'ImageGalleryThumbnails';

const styles = StyleSheet.create({
  container: {
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    alignItems: 'center',
  },
  listContent: {
    alignItems: 'center',
  },
  thumbnailContainer: {
    width: THUMBNAIL_SIZE,
    height: THUMBNAIL_SIZE,
    borderRadius: borderRadius.sm,
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  thumbnailActive: {
    borderColor: colors.brand.core,
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
  },
  thumbnailInactive: {
    opacity: 0.4,
  },
});
